import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { globalSearchAPI } from '../services/api';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';

const GROUP_LIST_PATHS = {
  clients:     '/lists/clients',
  providers:   '/lists/providers',
  contacts:    '/lists/contacts',
  schools:     '/lists/schools',
  at_packets:  '/lists/at-packets',
  file_reviews:'/lists/file-reviews',
  transfers:   '/lists/transfers',
  checklists:  '/my-checklists',
};

function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  /** Bumped on every query change so late responses from older searches are ignored (fixes flicker in prod where latency varies). */
  const searchSeqRef = useRef(0);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const indexed = useMemo(() => {
    let idx = 0;
    const grouped = (groups || []).map((group) => ({
      ...group,
      items: (group.items || []).map((item) => ({
        ...item,
        _idx: idx++,
        _key: `${group.type}-${item.type}-${item.id}-${item.url}`,
      })),
    }));
    return {
      grouped,
      flat: grouped.flatMap((group) => group.items),
    };
  }, [groups]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, [location.pathname]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  useEffect(() => {
    const q = query.trim();
    const mySeq = ++searchSeqRef.current;
    if (q.length < 2) {
      setGroups([]);
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }
    const timer = setTimeout(async () => {
      if (mySeq !== searchSeqRef.current) return;
      setLoading(true);
      try {
        const res = await globalSearchAPI.search({ q, perType: 5 });
        if (mySeq !== searchSeqRef.current) return;
        setGroups(res.data?.data?.groups || []);
        setActiveIndex(-1);
      } catch (err) {
        if (mySeq !== searchSeqRef.current) return;
        console.error('Global search failed:', err);
        setGroups([]);
      } finally {
        if (mySeq === searchSeqRef.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!dropdownRef.current || activeIndex < 0) return;
    const activeEl = dropdownRef.current.querySelector(`[data-search-idx="${activeIndex}"]`);
    if (activeEl && typeof activeEl.scrollIntoView === 'function') {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const goToResult = (url) => {
    if (!url) return;
    setOpen(false);
    setQuery('');
    setGroups([]);
    setActiveIndex(-1);
    navigate(url);
  };

  const onInputKeyDown = (e) => {
    if (!open) setOpen(true);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (indexed.flat.length === 0) return;
      setActiveIndex((prev) => (prev + 1) % indexed.flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (indexed.flat.length === 0) return;
      setActiveIndex((prev) => (prev <= 0 ? indexed.flat.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && indexed.flat[activeIndex]) {
        e.preventDefault();
        goToResult(indexed.flat[activeIndex].url);
      } else if (indexed.flat.length === 1) {
        e.preventDefault();
        goToResult(indexed.flat[0].url);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (query) {
        setQuery('');
        setGroups([]);
      } else {
        setOpen(false);
        setActiveIndex(-1);
      }
    }
  };

  const trimmed = query.trim();
  const showDropdown = open && (loading || trimmed.length > 0);

  return (
    <div className="nav-global-search" ref={containerRef}>
      <div className="nav-global-search-input-wrap">
        <span className="nav-global-search-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          className="nav-global-search-input"
          placeholder="Global search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKeyDown}
          aria-label="Global search"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          role="combobox"
        />
        {query ? (
          <button
            type="button"
            className="nav-global-search-clear"
            onClick={() => {
              setQuery('');
              setGroups([]);
              setActiveIndex(-1);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            title="Clear"
          >
            x
          </button>
        ) : null}
      </div>
      {showDropdown && (
        <div className="nav-global-search-dropdown" ref={dropdownRef}>
          {loading ? (
            <div className="nav-global-search-state">Searching...</div>
          ) : trimmed.length < 2 ? (
            <div className="nav-global-search-state">Type at least 2 characters</div>
          ) : indexed.grouped.length === 0 ? (
            <div className="nav-global-search-state">No matches found</div>
          ) : (
            indexed.grouped.map((group) => (
              <div key={group.type} className="nav-global-search-group">
                <div className="nav-global-search-group-title">{group.label}</div>
                {(group.items || []).map((item) => {
                  const active = item._idx === activeIndex;
                  return (
                    <button
                      key={item._key}
                      type="button"
                      className={`nav-global-search-item ${active ? 'active' : ''}`}
                      data-search-idx={item._idx}
                      onMouseEnter={() => setActiveIndex(item._idx)}
                      onClick={() => goToResult(item.url)}
                    >
                      <span className="nav-global-search-item-title">{item.title}</span>
                      {item.subtitle ? <span className="nav-global-search-item-subtitle">{item.subtitle}</span> : null}
                    </button>
                  );
                })}
                {GROUP_LIST_PATHS[group.type] && (
                  <button
                    type="button"
                    className="nav-global-search-see-all"
                    onClick={() => goToResult(tenantPath(`${GROUP_LIST_PATHS[group.type]}?q=${encodeURIComponent(trimmed)}`))}
                  >
                    See all {group.label.toLowerCase()} →
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
